<template>
  <div>
    <div v-if="isFormValid === false" class="error-block d-flex mb-2 align-center w-100">
      <v-icon class="mr-3 ml-4" icon="$warning"></v-icon>
      <span class="body2 text-red-dark">
        {{ $t("validations.bundleOrModuleIsRequired") }}
      </span>
    </div>

    <div v-if="singleBundleInfoVisible" class="info-block d-flex mb-2 align-center w-100">
      <v-icon class="mr-3 ml-4" icon="$warning"></v-icon>
      <span class="subtitle2">
        {{ $t("modals.createWorkspace.onlyOneBundleCanBeSelected") }}
      </span>
    </div>
    <!--Faking validation for bundles and modules-->
    <v-select
      :items="[SubscriptionBundle.CORO_CLASSIC]"
      :model-value="subscription.bundles"
      multiple
      :rules="[bundleValidationRule()]"
      class="d-none"
      :validation-value="moduleOrBundleValidationValue"
    >
    </v-select>
    <v-tabs
      v-model="selectedTab"
      align-with-title
      class="edit-subscription-tabs"
      color="orange-base"
      background-color="white"
      slider-color="orange-base"
      :height="48"
    >
      <v-tab v-for="item in tabs" :key="item.value" :value="item.value" class="mr-8" :height="42">
        {{ item.title }}
      </v-tab>
    </v-tabs>
    <v-window v-model="selectedTab">
      <v-window-item v-for="item in tabs" :key="item.value" :value="item.value">
        <div v-if="selectedTab === UpdateSubscriptionCardTabValue.MODULES" class="mt-4 pb-2">
          <div class="subtitle1 mb-4" v-if="atLeastOneModuleNotSelected">
            {{ $t("modals.createWorkspace.recommendedModules") }}
          </div>
          <div class="d-flex flex-wrap row-gap">
            <template v-for="(module, moduleName) in modulesAllowedToSelect" :key="moduleName">
              <cui-module-or-addon-selector
                v-model="module.enabled"
                :name="moduleName"
                :title="$t(`subscriptions.modules.${moduleName}`)"
                :subtitle="$t(`moduleOrAddonSelector.${moduleName}.description`)"
                :select-btn-title="$t('general.select')"
                :selected-btn-title="$t('general.selected')"
                v-if="!moduleIncludedInBundle(moduleName)"
              >
                <template #content:after>
                  <div class="mt-5">
                    <template v-for="addon in module.addons" :key="addon.name">
                      <div class="coro-btn-group">
                        <button-toggle-group
                          v-if="isSocAddon(addon.name)"
                          v-model="addon.enabled"
                          :items="socAddonToggleItems"
                          density="comfortable"
                          class="w-100"
                        />
                      </div>
                    </template>
                  </div>
                </template>
              </cui-module-or-addon-selector>
            </template>
          </div>

          <template v-if="showAddonsSelection">
            <v-divider class="mt-8"></v-divider>
            <div class="subtitle1 mb-4 mt-4">
              {{ $t("modals.createWorkspace.addons") }}
            </div>

            <div class="d-flex flex-wrap row-gap">
              <template v-for="(module, moduleName) in modulesAllowedToSelect" :key="moduleName">
                <template v-for="addon in module.addons" :key="addon.name">
                  <cui-module-or-addon-selector
                    :is-addon="true"
                    v-model="addon.enabled"
                    :name="addon.name"
                    :disabled="!module.enabled"
                    :addon-label="$t('general.addon')"
                    :title="$t(`subscriptions.addons.${addon.name}`)"
                    :subtitle="$t(`moduleOrAddonSelector.${addon.name}.description`)"
                    :select-btn-title="$t('general.select')"
                    :selected-btn-title="$t('general.selected')"
                    v-if="!isSocAddon(addon.name) && !addonIncludedInBundle(addon.name)"
                  >
                    <template #content:after>
                      <div
                        class="info-block info-block--large align-center mt-5 d-flex"
                        v-if="!module.enabled"
                      >
                        <v-icon class="mr-3 ml-4" icon="$warning"></v-icon>
                        <span
                          class="body2 text-primary mr-2"
                          v-html="
                            $t(`moduleOrAddonSelector.addonDisabled`, {
                              module: $t(`subscriptions.modules.${moduleName}`),
                            })
                          "
                        ></span>
                      </div>
                    </template>
                  </cui-module-or-addon-selector>
                </template>
              </template>
            </div>
          </template>

          <template v-if="hasAtLeastOneModuleIncludedInBundle">
            <v-divider v-if="atLeastOneModuleNotSelected" class="mt-8"></v-divider>
            <div class="subtitle1 mb-4 mt-4">
              {{ $t("modals.createWorkspace.includedInBundles") }}
            </div>
            <div class="d-flex flex-wrap row-gap">
              <template
                v-for="moduleName in getSelectedFilteredModules(subscription)"
                :key="moduleName"
              >
                <cui-module-or-addon-selector
                  :model-value="false"
                  :name="moduleName"
                  :title="$t(`subscriptions.modules.${moduleName}`)"
                  :subtitle="$t(`moduleOrAddonSelector.${moduleName}.description`)"
                  v-if="moduleIncludedInBundle(moduleName)"
                >
                  <template #content:after>
                    <template v-if="moduleIncludedInBundle(moduleName)">
                      <div class="mt-5" v-if="!isModuleIncludedInManagedBundle(moduleName)">
                        <template
                          v-for="addon in subscription.modules[moduleName].addons"
                          :key="addon.name"
                        >
                          <div class="coro-btn-group">
                            <button-toggle-group
                              v-if="isSocAddon(addon.name)"
                              v-model="addon.enabled"
                              :items="socAddonToggleItems"
                              density="comfortable"
                              class="w-100"
                            />
                          </div>
                        </template>
                      </div>
                    </template>
                  </template>
                  <template #footer>
                    <div class="w-100">
                      <v-divider class="mt-3 mb-3"></v-divider>
                      <div class="d-flex align-center justify-center">
                        <v-icon class="mr-2" icon="$check"></v-icon>
                        <span class="text-uppercase text--semibold body2">
                          {{ $t("modals.createWorkspace.includedInBundle") }}
                        </span>
                      </div>
                    </div>
                  </template>
                </cui-module-or-addon-selector>
              </template>
              <template v-for="addon in getSelectedAddons()" :key="addon">
                <cui-module-or-addon-selector
                  :model-value="false"
                  :is-addon="true"
                  :name="addon"
                  :addon-label="$t('general.addon')"
                  :title="$t(`subscriptions.addons.${addon}`)"
                  :subtitle="$t(`moduleOrAddonSelector.${addon}.description`)"
                  v-if="addonIncludedInBundle(addon) && !isSocAddon(addon)"
                >
                  <template #footer>
                    <div class="w-100">
                      <v-divider class="mt-3 mb-3"></v-divider>
                      <div class="d-flex align-center justify-center">
                        <v-icon class="mr-2" icon="$check"></v-icon>
                        <span class="text-uppercase text--semibold body2">
                          {{ $t("modals.createWorkspace.includedInBundle") }}
                        </span>
                      </div>
                    </div>
                  </template>
                </cui-module-or-addon-selector>
              </template>
              <!--Displaying only one SOC addon -->
              <cui-module-or-addon-selector
                :is-addon="true"
                :icon="SUBSCRIPTION_MANAGED_SERVICE_ICON"
                :addon-label="$t('general.addon')"
                :title="$t('subscriptions.addons.soc')"
                :subtitle="$t('moduleOrAddonSelector.soc.description')"
                :model-value="false"
                v-if="hasAtLeastOneSocAddon"
              >
                <template #footer>
                  <div class="w-100">
                    <v-divider class="mt-3 mb-3"></v-divider>
                    <div class="d-flex align-center justify-center">
                      <v-icon class="mr-2" icon="$check"></v-icon>
                      <span class="text-uppercase text--semibold body2">
                        {{ $t("modals.createWorkspace.includedInBundle") }}
                      </span>
                    </div>
                  </div>
                </template>
              </cui-module-or-addon-selector>
            </div>
          </template>
        </div>
        <div
          v-else-if="selectedTab === UpdateSubscriptionCardTabValue.LEGACY_BUNDLES"
          class="d-flex mt-4 pb-2 row-gap flex-wrap"
        >
          <cui-range-bundle-selector
            v-for="config in filteredLegacyBundlesConfig"
            :key="config.unmanagedOption"
            :managed-option="config.managedOption"
            :unmanaged-option="config.unmanagedOption"
            :title="$t(`manageWorkspaces.bundles.${config.unmanagedOption}`)"
            :description="$t(`rangeBundleSelector.${config.unmanagedOption}.description`)"
            :select-label="$t('general.select')"
            :selected-label="$t('general.selected')"
            :unmanaged-label="$t('rangeBundleSelector.unmanaged')"
            :managed-label="$t('rangeBundleSelector.managed')"
            :managed-tooltip="$t('rangeBundleSelector.managedTooltip')"
            :show-sat="!isCanadaEnv"
            v-model:selected-bundles="selectedBundles"
          >
            <template #moduleLabel="{ value }">{{ $t(`subscriptions.modules.${value}`) }}</template>
            <template #addonLabel="{ value }">{{ $t(`subscriptions.addons.${value}`) }}</template>
            <template #managedIndicator>{{ $t("subscriptions.addons.soc") }}</template>
          </cui-range-bundle-selector>
        </div>
        <div
          v-else-if="selectedTab === UpdateSubscriptionCardTabValue.BUNDLES"
          class="d-flex mt-4 pb-2 row-gap flex-wrap"
        >
          <cui-range-bundle-selector
            v-for="config in filteredNewBundlesConfig"
            :key="config.unmanagedOption"
            :managed-option="config.managedOption"
            :unmanaged-option="config.unmanagedOption"
            :title="$t(`manageWorkspaces.bundles.${config.unmanagedOption}`)"
            :description="$t(`rangeBundleSelector.${config.unmanagedOption}.description`)"
            :select-label="$t('general.select')"
            :selected-label="$t('general.selected')"
            :unmanaged-label="$t('rangeBundleSelector.unmanaged')"
            :managed-label="$t('rangeBundleSelector.managed')"
            :managed-tooltip="$t('rangeBundleSelector.managedTooltip')"
            :show-sat="!isCanadaEnv"
            v-model:selected-bundles="selectedBundles"
          >
            <template #moduleLabel="{ value }">{{ $t(`subscriptions.modules.${value}`) }}</template>
            <template #addonLabel="{ value }">{{ $t(`subscriptions.addons.${value}`) }}</template>
            <template #managedIndicator>{{ $t("subscriptions.addons.soc") }}</template>
          </cui-range-bundle-selector>
        </div>
      </v-window-item>
    </v-window>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType, ref, type Ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { SubscriptionBundle, SubscriptionModule } from "@/constants/workplaces";
import {
  CuiModuleOrAddonSelector,
  CuiRangeBundleSelector,
  getLegacyBundles,
  getManagedBundleVariant,
  getNonLegacyBundles,
  isLegacyBundle,
  isModuleInSelectedBundles,
  SUBSCRIPTION_MANAGED_SERVICE_ICON,
  useCuiSubscriptionEditor,
} from "@coronet/cui";
import ButtonToggleGroup, { type ButtonToggleItem } from "@/components/ButtonToggleGroup.vue";
import pick from "lodash/pick";
import cloneDeep from "lodash/cloneDeep";
import isEqual from "lodash/isEqual";
import type { SubscriptionModuleInfo } from "@/_store/workspace.module.ts";
import type { Subscription, SubscriptionRulesConfig } from "@/_store/msp/msp.module.ts";
import { isCanadaEnv } from "@/constants/general.ts";
import difference from "lodash/difference";
import uniq from "lodash/uniq";

// Base (unmanaged) → managed bundle pairs, derived from the library SoT so they
// can't drift from BUNDLE_DEFINITIONS. `getManagedBundleVariant` is truthy only
// for base bundles that have a managed twin, which filters out everything else.
const toBundleConfig = (bundle: SubscriptionBundle): BundleConfig => ({
  unmanagedOption: bundle,
  managedOption: getManagedBundleVariant(bundle)!,
});

const NEW_BUNDLES_CONFIGS: BundleConfig[] = getNonLegacyBundles()
  .filter((bundle) => getManagedBundleVariant(bundle))
  .map(toBundleConfig);

const LEGACY_BUNDLES_CONFIGS: BundleConfig[] = getLegacyBundles()
  .filter((bundle) => getManagedBundleVariant(bundle))
  .map(toBundleConfig);

type BundleConfig = {
  unmanagedOption: SubscriptionBundle;
  managedOption: SubscriptionBundle;
};

// The server rejects any selection holding more than one of these: a Complete bundle owns the
// subscription on its own, and only one new (AI) bundle may ever be selected. Several ordinary
// legacy bundles together stay valid, which is why the predicate is not simply "is legacy".
const isExclusiveBundle = (bundle: SubscriptionBundle) =>
  !isLegacyBundle(bundle) ||
  bundle === SubscriptionBundle.CORO_COMPLETE ||
  bundle === SubscriptionBundle.MANAGED_CORO_COMPLETE;

export enum UpdateSubscriptionCardTabValue {
  BUNDLES = "tab-bundles",
  LEGACY_BUNDLES = "tab-legacy-bundles",
  MODULES = "tab-modules",
}

type UpdateSubscriptionCardTab = {
  title: string;
  value: UpdateSubscriptionCardTabValue;
  show: boolean;
};

export default defineComponent({
  components: {
    ButtonToggleGroup,
    CuiModuleOrAddonSelector,
    CuiRangeBundleSelector,
  },
  props: {
    subscription: {
      type: Object as PropType<Subscription>,
      required: true,
    },
    subscriptionRules: {
      type: Object as PropType<SubscriptionRulesConfig>,
      default: (): SubscriptionRulesConfig => ({
        allowedBundles: Object.values(SubscriptionBundle),
        allowedModules: Object.values(SubscriptionModule),
      }),
    },
    isFormValid: {
      type: Boolean,
      default: true,
    },
    singleBundleSelection: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:subscription"],
  setup(props, { emit }) {
    const i18n = useI18n();
    const selectedTab = ref<UpdateSubscriptionCardTabValue>(UpdateSubscriptionCardTabValue.BUNDLES);

    const editableSubscription: Ref<Subscription> = ref(cloneDeep(props.subscription));
    const subscriptionInitialState = cloneDeep(props.subscription);

    watch(
      editableSubscription,
      (newValue) => {
        emit("update:subscription", newValue);
      },
      { deep: true }
    );

    watch(
      () => props.subscription,
      (newValue) => {
        if (!isEqual(newValue, editableSubscription.value)) {
          editableSubscription.value = cloneDeep(newValue);
        }
      },
      { deep: true }
    );

    const {
      moduleIncludedInBundle,
      addonIncludedInBundle,
      isSocAddon,
      hasAtLeastOneSocAddon,
      hasAtLeastOneModuleIncludedInBundle,
      isModuleIncludedInManagedBundle,
      getEnabledModules,
      getSelectedAddons,
    } = useCuiSubscriptionEditor(editableSubscription, (newData) => {
      emit("update:subscription", newData);
    });

    const filterBundleConfigs = (configs: BundleConfig[]): BundleConfig[] => {
      const allowedBundles = props.subscriptionRules?.allowedBundles ?? [];
      return configs.filter((bundle) => {
        const isAllowed =
          allowedBundles.includes(bundle.unmanagedOption) ||
          allowedBundles.includes(bundle.managedOption);
        const isCurrentlyEnabled =
          subscriptionInitialState.bundles.includes(bundle.unmanagedOption) ||
          subscriptionInitialState.bundles.includes(bundle.managedOption);
        return isCurrentlyEnabled ? true : isAllowed;
      });
    };

    const modulesAllowedToSelect = computed(() => {
      // Include modules that were standalone-enabled at load time but may no longer be in
      // allowedModules (e.g. admin removed the module from rules after the workspace had already
      // enabled it). Using subscriptionInitialState means a module disabled during the current
      // edit session won't reappear until the next load — matching the same behavior as bundles.
      // Modules enabled via a bundle are excluded: they are not standalone purchases.
      const currentlyEnabledModules = (
        Object.keys(subscriptionInitialState.modules) as SubscriptionModule[]
      ).filter(
        (moduleName) =>
          subscriptionInitialState.modules[moduleName].enabled &&
          // The module is not part of any selected bundle — it was enabled standalone.
          !isModuleInSelectedBundles(moduleName, subscriptionInitialState.bundles)
      );

      // Standalone modules and bundles are independent — only explicitly allowed modules appear
      // here, plus any that were standalone-enabled before the rules changed.
      const modulesToShow = uniq([
        ...(props.subscriptionRules.allowedModules ?? []),
        ...currentlyEnabledModules,
      ]).filter((m) => {
        // TODO: Remove crutch once SAT will be available in Canada
        if (isCanadaEnv) {
          return m !== SubscriptionModule.SECURITY_AWARENESS_TRAINING;
        }
        return true;
      });

      // Pick from editableSubscription so module state (enabled, addons) stays reactive to user edits.
      return pick(editableSubscription.value.modules, modulesToShow);
    });

    const filteredNewBundlesConfig = computed(() => filterBundleConfigs(NEW_BUNDLES_CONFIGS));
    const filteredLegacyBundlesConfig = computed(() => filterBundleConfigs(LEGACY_BUNDLES_CONFIGS));

    const tabs = computed(() => {
      const allTabs: UpdateSubscriptionCardTab[] = [
        {
          title: i18n.t("subscriptions.bundles.title"),
          value: UpdateSubscriptionCardTabValue.BUNDLES,
          show: filteredNewBundlesConfig.value.length > 0,
        },
        {
          title: i18n.t("subscriptions.legacyBundles"),
          value: UpdateSubscriptionCardTabValue.LEGACY_BUNDLES,
          show: filteredLegacyBundlesConfig.value.length > 0,
        },
        {
          title: i18n.t("subscriptions.modules.title"),
          value: UpdateSubscriptionCardTabValue.MODULES,
          show:
            Object.keys(modulesAllowedToSelect.value).length > 0 ||
            hasAtLeastOneModuleIncludedInBundle.value,
        },
      ];
      return allTabs.filter((tab) => tab.show);
    });

    watch(
      tabs,
      (newTabs) => {
        const selectedTabExists = newTabs.some((tab) => tab.value === selectedTab.value);
        if (!selectedTabExists && newTabs.length > 0) {
          selectedTab.value = newTabs[0].value;
        }
      },
      { immediate: true }
    );

    const atLeastOneModuleNotSelected = computed(() => {
      return Object.values(modulesAllowedToSelect.value).some(
        (module: SubscriptionModuleInfo) => !module.enabled
      );
    });

    const socAddonToggleItems = computed<ButtonToggleItem[]>(() => [
      {
        value: false,
        label: i18n.t("moduleOrAddonSelector.unmanaged"),
      },
      {
        value: true,
        label: i18n.t("moduleOrAddonSelector.managed"),
        iconAfterLabel: "$info",
        iconAfterLabelTooltip: i18n.t("moduleOrAddonSelector.managedTooltip"),
      },
    ]);

    const showAddonsSelection = computed(() => {
      const modules = Object.values(modulesAllowedToSelect.value);
      // Skip rendering the addons section entirely if no allowed module has any addons configured.
      const hasAnyAddons = modules.some(
        (module: SubscriptionModuleInfo) => module.addons.length > 0
      );
      if (!hasAnyAddons) return false;
      // Show the section only if at least one addon is selectable standalone:
      // SOC addons are shown inline inside their module card, and bundle-included addons
      // appear in the "Included in bundle" section — neither belongs here.
      return modules.some((module: SubscriptionModuleInfo) => {
        return module.addons.some(
          (addon) => !isSocAddon(addon.name) && !addonIncludedInBundle(addon.name)
        );
      });
    });

    const moduleOrBundleValidationValue = computed(() => {
      const isModuleEnabled = Object.values(editableSubscription.value.modules).some(
        (module: SubscriptionModuleInfo) => module.enabled
      );
      return editableSubscription.value.bundles.length > 0 || isModuleEnabled;
    });

    const bundleValidationRule = (
      errorMessage: string = i18n.t("validations.bundleOrModuleIsRequired")
    ) => {
      return (value: boolean) => (value ? true : errorMessage);
    };

    const getSelectedFilteredModules = (subscription: Subscription): SubscriptionModule[] => {
      // TODO: Remove crutch once SAT will be available in Canada
      const modules = getEnabledModules(subscription);
      if (isCanadaEnv) {
        return modules.filter((m) => m !== SubscriptionModule.SECURITY_AWARENESS_TRAINING);
      }
      return modules;
    };

    const singleBundleInfoVisible = computed(() => {
      // Hide bundle-only warning on the Modules tab.
      if (selectedTab.value === UpdateSubscriptionCardTabValue.MODULES) {
        return false;
      }
      if (props.singleBundleSelection) {
        return editableSubscription.value.bundles.length > 0;
      }
      return editableSubscription.value.bundles.some(isExclusiveBundle);
    });

    /**
     * Keeps the selection to something the server will accept.
     *
     * Works from the incoming array rather than rebuilding from the current one: the
     * Managed/Unmanaged toggle emits an array where a bundle has been replaced by its twin, and
     * rebuilding would resurrect the base variant alongside the managed one.
     */
    const updateSelectedBundles = (bundles: SubscriptionBundle[]) => {
      if (props.singleBundleSelection) {
        const addedBundles = difference(bundles, editableSubscription.value.bundles);
        if (addedBundles.length > 0) {
          editableSubscription.value.bundles = [addedBundles[0]];
          return;
        }
        editableSubscription.value.bundles = bundles.length === 0 ? [] : [bundles[0]];
        return;
      }

      const added = difference(bundles, editableSubscription.value.bundles);
      if (!added.length) {
        // A pure deselection: nothing was added relative to the current selection (a
        // managed/unmanaged swap always introduces the new variant's enum value, so it never
        // lands here — it falls through to the exclusive-bundle lookup below), so the incoming
        // array, with some bundles possibly removed, is already valid as-is.
        editableSubscription.value.bundles = bundles;
        return;
      }

      const exclusive = added.find(isExclusiveBundle);
      editableSubscription.value.bundles = exclusive
        ? [exclusive]
        : bundles.filter((bundle) => !isExclusiveBundle(bundle));
    };

    const selectedBundles = computed({
      get: () => editableSubscription.value.bundles,
      set: updateSelectedBundles,
    });

    return {
      selectedTab,
      editableSubscription,
      tabs,
      filteredNewBundlesConfig,
      filteredLegacyBundlesConfig,
      modulesAllowedToSelect,
      atLeastOneModuleNotSelected,
      showAddonsSelection,
      moduleOrBundleValidationValue,
      bundleValidationRule,
      moduleIncludedInBundle,
      addonIncludedInBundle,
      isSocAddon,
      hasAtLeastOneSocAddon,
      hasAtLeastOneModuleIncludedInBundle,
      isModuleIncludedInManagedBundle,
      getEnabledModules,
      getSelectedAddons,
      SubscriptionBundle,
      SUBSCRIPTION_MANAGED_SERVICE_ICON,
      isCanadaEnv,
      getSelectedFilteredModules,
      selectedBundles,
      singleBundleInfoVisible,
      socAddonToggleItems,
      UpdateSubscriptionCardTabValue,
    };
  },
});
</script>

<style lang="scss" scoped>
:deep(*) {
  .v-chip__close {
    .icon-rules-close:before {
      color: rgb(var(--v-theme-primary));
    }
  }

  .v-btn-group--density-default.v-btn-group {
    height: unset !important;
  }

  .bundle-selector {
    background-color: white;
  }

  .button-toggle-wrapper {
    width: 100%;
  }
}

.summary-item {
  border: 1px solid rgb(var(--v-theme-indigo-faint)) !important;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
}

.progress-bar__content {
  width: 200px;
}

.included-in-bundle-block {
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  text-transform: uppercase;
  font-weight: 600;
}

.row-gap {
  gap: 23px;
}
.info-block--large {
  min-height: 68px;
}
</style>
